const DEFAULT_PROD_API_BASE_URL = 'https://ss-api.ventaplaza.com/api';
const DEFAULT_DEV_API_BASE_URL = 'http://127.0.0.1:8000/api';

const isLocalApiUrl = (value) => {
  if (!value) return false;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\b/i.test(value);
};

const normalizeApiBase = (value) => {
  if (!value) return value;
  // trim trailing slashes
  let v = String(value).trim().replace(/\/+$/, '');
  // if someone provides origin without /api, accept it but normalize to /api
  if (!/\/api$/i.test(v)) v = `${v}/api`;
  return v;
};

// CRA env vars are baked at build-time. Guard against accidental production builds
// that were built with REACT_APP_API_URL pointing to localhost/127.0.0.1.
const envApi = normalizeApiBase(process.env.REACT_APP_API_URL);

const API_BASE_URL =
  (process.env.NODE_ENV === 'production' && isLocalApiUrl(envApi))
    ? DEFAULT_PROD_API_BASE_URL
    : (envApi ||
      (process.env.NODE_ENV === 'production'
        ? DEFAULT_PROD_API_BASE_URL
        : DEFAULT_DEV_API_BASE_URL));

export const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, '');

export const storageUrl = (filePath = '') => {
  let cleaned = String(filePath || '')
    .replace(/^https?:\/\/[^/]+/i, '') // strip origin if present
    .replace(/^\/?storage\//i, '') // strip storage prefix if present
    .replace(/^\/+/, '');
  // If path is a bare filename (e.g. from some admin flows), prefix with properties/
  if (cleaned && !cleaned.includes('/') && !/^background-images\//.test(cleaned)) {
    cleaned = 'properties/' + cleaned;
  }
  // Prefer Laravel media endpoint because some servers block /storage/* (403)
  return `${API_ORIGIN}/api/media/${cleaned}`;
};

// Normalize any media-like URL (absolute or relative) to the /api/media endpoint.
export const normalizeMediaUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const hasOrigin = /^https?:\/\//i.test(raw);
  const withoutOrigin = raw.replace(/^https?:\/\/[^/]+/i, '');

  if (/\/api\/media\//i.test(raw)) {
    // Always use API_ORIGIN so images load from the same host the app uses (avoids 403 from wrong domain)
    const match = raw.match(/\/api\/media\/(.+)$/i);
    const pathPart = match ? match[1].replace(/^\/+/, '') : raw.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '');
    return `${API_ORIGIN}/api/media/${pathPart}`;
  }

  if (/\/storage\//i.test(raw)) {
    const cleaned = withoutOrigin.replace(/^\/?storage\//i, '');
    return `${API_ORIGIN}/api/media/${cleaned.replace(/^\/+/, '')}`;
  }

  // If we receive a relative media path (e.g. "properties/123.jpg" or bare "xyz.jpg"), route it to /api/media.
  if (!hasOrigin) {
    let cleaned = raw.replace(/^\/+/, '');
    if (cleaned && !cleaned.includes('/') && !/^background-images\//.test(cleaned)) {
      cleaned = 'properties/' + cleaned;
    }
    return `${API_ORIGIN}/api/media/${cleaned}`;
  }

  return raw;
};

// Direct public-disk URL (requires correct webserver config + storage symlink).
export const publicStorageUrl = (filePath = '') => {
  const cleaned = String(filePath || '')
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/?storage\//i, '');
  return `${API_ORIGIN}/storage/${cleaned.replace(/^\/+/, '')}`;
};

// Helper for <img onError> to try /storage/ when /api/media/ fails.
export const withStorageFallback = (url = '') => {
  const u = String(url || '');
  if (u.includes('/api/media/')) return u.replace('/api/media/', '/storage/');
  return u;
};

export const apiUrl = (path = '') => {
  if (!path) return API_BASE_URL;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export default API_BASE_URL;
